// typings
import {
  EPaymentBrand,
  ContractClassification,
  EContractType,
  ECurrency,
  EFeePayee,
  EReturnPolicy,
  ESenderRole,
  EShipmentType,
  IContractDetailsBody,
  ItemDetailsDTO,
  EPaymentType,
} from "../api/services/contract/models";
import { browserName } from "./checkBrowserCompatible";
import imageCompression from "browser-image-compression";

export const MAX_FILE_SIZE = 10 * 1005 * 1005; // 10 MB in bytes

export const contractBody = (
  title: ItemDetailsDTO["title"],
  description: ItemDetailsDTO["description"],
  price: IContractDetailsBody["itemPrice"],
  shippingFee: IContractDetailsBody["shipment"]["fee"],
  termsAccepted: boolean,
  shipmentType?: EShipmentType,
  interactiveContract?: boolean,
  quantity?: number,
  contractType?: "General" | "Haraj" | "Landing" | "Stock" | "Haraj_Stock"
): IContractDetailsBody => {
  return {
    type: EContractType.QUICK_TRANSACTION,
    senderRole: ESenderRole.PROVIDER,
    itemDetail: {
      title: title,
      description: description,
      images: [],
    },
    itemPrice: price,
    currency: ECurrency.SAR,
    feePayee: EFeePayee.PROVIDER,
    shipment: {
      type: EShipmentType.NONE,
      fee: shippingFee,
      currency: ECurrency.SAR,
    },
    returnPolicy: EReturnPolicy.NO_RETURN,
    contractClassification: typeMap[contractType || "General"],
    returnFeePayee: EFeePayee.PROVIDER,
    receiverId: "",
    receiverName: "",
    waffyTermsAccepted: termsAccepted,
    quantity: quantity,
  };
};

export const handleCompressImage = async (
  imageFile: File | undefined
): Promise<File | void> => {
  if (!imageFile) return;

  const options = {
    maxSizeMB: 2,
    maxWidthOrHeight: 1920,
    useWebWorker: browserName() === "Safari" ? false : true,
  };

  try {
    const compressedFile = await imageCompression(imageFile, options);
    return compressedFile;
  } catch (error) {
    console.log(error);
    return;
  }
};

export const brokerContractBody = (
  title: ItemDetailsDTO["title"],
  description: ItemDetailsDTO["description"],
  price: IContractDetailsBody["itemPrice"],
  brokerFee: IContractDetailsBody["brokerFee"],
  termsAccepted: boolean
): Partial<IContractDetailsBody> => {
  return {
    type: EContractType.BROKERAGE_CONTRACT,
    senderRole: ESenderRole.BROKER,
    itemDetail: {
      title: title,
      description: description,
      images: [],
    },
    itemPrice: price,
    currency: ECurrency.SAR,
    returnPolicy: EReturnPolicy.NO_RETURN,
    returnFeePayee: EFeePayee.PROVIDER,
    receiverId: "",
    receiverName: "",
    brokerFee,
    waffyTermsAccepted: termsAccepted,
  };
};

export const validateDate = (
  value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
) => {
  if (/[٠-٩]/.test(value)) return true;
  if (/[0-9]/.test(value)) return true;
  if (value === "-" || value === "/") return true;
  return false;
};

export const isValidDateFormat = (inputString: string) => {
  // Regular expression to match either "dd-mm-yyyy" or "dd/mm/yyyy" format
  const dateFormatRegex =
    /^(0[1-9]|[12][0-9]|3[01])[-\/](0[1-9]|1[0-2])[-\/]\d{4}$/;

  return dateFormatRegex.test(inputString);
};

export const convertToArabic = (englishNumber) => {
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

  return String(englishNumber).replace(/\d/g, (digit) => arabicDigits[digit]);
};

export const configureNumber = (number) => {
  const arabicNumbers = [
    /٠/g,
    /١/g,
    /٢/g,
    /٣/g,
    /٤/g,
    /٥/g,
    /٦/g,
    /٧/g,
    /٨/g,
    /٩/g,
  ];
  if (typeof number === "string") {
    for (let i = 0; i < arabicNumbers.length; i++) {
      number = number.replace(arabicNumbers[i], i);
    }
  }
  return number;
};

export const validateNumber = (
  value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
) => {
  if (/[0-9]/.test(value)) return true;
  return false;
};

export const convertDate = (date: string) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("ar-eg");
};

// Helper to normalize value (handle arrays, null, undefined)
export const normalizeValue = (
  val: string | number | readonly string[] | null | undefined
): string | number => {
  if (val == null) return "";
  if (Array.isArray(val)) return val.join(",");
  return val as string | number;
};

export const formatDateToArabic = (isoDateString?: string): string => {
  if (!isoDateString) return "";
  // Months names in Arabic
  const arabicMonths = [
    "يناير",
    "فبراير",
    "مارس",
    "أبريل",
    "مايو",
    "يونيو",
    "يوليو",
    "أغسطس",
    "سبتمبر",
    "أكتوبر",
    "نوفمبر",
    "ديسمبر",
  ];

  // Days names in Arabic
  const arabicDays = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];

  // Parse ISO date string
  const date = new Date(isoDateString);

  // Get day, month, and year
  const day = date.getDate();
  const month = date.getMonth();

  // Get day name and month name in Arabic
  const dayName = arabicDays[date.getDay()];
  const monthName = arabicMonths[month];

  // Construct the formatted date string
  const formattedDate = `${dayName} ${day} ${monthName}`;

  return formattedDate;
};

export const typeMap = {
  General: ContractClassification.BASIC,
  Landing: ContractClassification.BASIC,
  Haraj: ContractClassification.BASIC_HARAJ,
  Stock: ContractClassification.STOCK_CONTRACT,
  Haraj_Stock: ContractClassification.STOCK_CONTRACT_HARAJ,
};

export const complexContractBody = (
  senderRole: ESenderRole,
  title: ItemDetailsDTO["title"],
  description: ItemDetailsDTO["description"],
  shipmentType: EShipmentType,
  isDraft?: boolean,
  interactiveContract?: boolean,
  quantity?: number,
  tabbyAllowed?: boolean,
  category?: {
    id: ItemDetailsDTO["category"] | string | number;
    name: string;
  },
  contractType?: "General" | "Haraj" | "Landing" | "Stock" | "Haraj_Stock"
): IContractDetailsBody => {
  return {
    type: EContractType.COMPLEX_CONTRACT,
    senderRole,
    itemDetail: {
      title: title,
      description: description,
      images: [],
    },
    itemPrice: "",
    draft: isDraft,
    shipment: {
      type: EShipmentType.NONE,
      fee: 0,
      currency: ECurrency.SAR,
    },
    returnPolicy: EReturnPolicy.NO_RETURN,
    returnFeePayee: EFeePayee.PROVIDER,
    receiverId: "",
    receiverName: "",
    waffyTermsAccepted: true,
    quantity: quantity,
    contractClassification: typeMap[contractType || "General"],

    currency: ECurrency.SAR,
  };
};

export const milestoneContractBody = (
  senderRole: ESenderRole,
  title: ItemDetailsDTO["title"],
  images: ItemDetailsDTO["images"],
  attachments: ItemDetailsDTO["attachments"],
  price: number,
  paymentMethods: {
    cards: EPaymentBrand[];
    apple_pay: EPaymentBrand[];
    tabby: EPaymentBrand[];
    bank_transfer: EPaymentBrand[];
  },
  deadline: string,
  milestoneCancelTerms?: string,
  milestoneReleaseTerms?: string,
  isDraft?: boolean,
  isAcceptable?: boolean,
  isInspectable?: boolean,
  currentStep?: string,
  quantity?: number,
  contractType?: "General" | "Haraj" | "Landing" | "Stock" | "Haraj_Stock"
): IContractDetailsBody => {
  return {
    type: EContractType.MILESTONE_CONTRACT,
    senderRole,
    itemDetail: {
      title: title || "",
      description: "",
      images,
      attachments,
    },
    itemPrice: price,
    draft: isDraft,
    isAcceptable,
    isInspectable,
    shipment: {
      type: EShipmentType.NONE,
      fee: 0,
      currency: ECurrency.SAR,
    },
    returnPolicy: EReturnPolicy.NO_RETURN,
    returnFeePayee: EFeePayee.PROVIDER,
    currentStep: currentStep || "",
    receiverId: "",
    receiverName: "",
    waffyTermsAccepted: true,
    quantity: quantity,
    contractClassification: typeMap[contractType || "General"],
    paymentMethods: {
      ...(paymentMethods?.cards && paymentMethods?.cards?.length > 0
        ? {
            [EPaymentType.MOYASAR]: [...paymentMethods.cards],
          }
        : {}),
      ...(paymentMethods?.bank_transfer &&
      paymentMethods?.bank_transfer?.length > 0
        ? {
            [EPaymentType.MANUAL_BANK_TRANSFER]: [
              ...paymentMethods.bank_transfer,
            ],
          }
        : {}),
      ...(paymentMethods?.cards && paymentMethods?.cards?.length > 0
        ? {
            [EPaymentType.APPLE_PAY]: [...paymentMethods.cards],
          }
        : {}),

      ...(paymentMethods?.tabby && paymentMethods?.tabby?.length > 0
        ? {
            [EPaymentType.TABBY]: [...paymentMethods.tabby],
          }
        : {}),
    },
    ...(paymentMethods?.tabby && paymentMethods?.tabby?.length > 0
      ? {
          metaData: {
            tabbyAllowed: "true",
          },
        }
      : {}),
    currency: ECurrency.SAR,
    deadLine: deadline ? new Date(deadline).toISOString() : "",
    acceptanceConditions: milestoneReleaseTerms,
    cancellationConditions: milestoneCancelTerms,
  };
};

export const getMappedContractType = (contractType: EContractType): string => {
  switch (contractType) {
    case EContractType.QUICK_TRANSACTION:
      return "عادي";
    case EContractType.BROKERAGE_CONTRACT:
      return "وسيط";
    case EContractType.COMPLEX_CONTRACT:
      return "مشروع";
    case EContractType.MILESTONE_CONTRACT:
      return "معاملة";
    default:
      return "";
  }
};
